
// Part category types
export type PartCategory = "k1_series" | "k2_series" | "tools";

// Part subcategory types
export type PartSubcategory = 
  | "hotend" 
  | "extruder" 
  | "probe" 
  | "tools" 
  | "fan"
  | "filter"
  | "power_supply"
  | "bed"
  | "build_plate"
  | "gantry"
  | "z_setup"
  | "nozzle_wiper"
  | "multi_filament"
  | "hinges"
  | "bed_fans"
  | "part_cooling_fan_shroud"
  | "part_cooling_fan"
  | "led"
  | "riser"
  | "feet";

// Part filter types
export type PartFilter = 
  | "upgrade" 
  | "eddy-current" 
  | "clicky" 
  | "unicorn" 
  | "stock" 
  | "wiring"
  | "requires-root"
  | "no-root"
  | "requires-printing"
  | "requires-tools"
  | "requires-purchase"
  | "printed"
  | "purchased"
  | "tools";

// AliExpress links interface
export interface AliExpressLinks {
  global?: string;
  us?: string;
}

// Part interface
export interface Part {
  id: number;
  name: string;
  description: string;
  category: PartCategory;
  subcategory: PartSubcategory;
  filters: PartFilter[];
  rating: number;
  requiresRoot: boolean;
  requiresWiring?: boolean;
  requiresPrinting?: boolean;
  requiresTools?: boolean;
  requiresPurchase?: boolean;
  amazonLink?: string;
  aliExpressLink?: string | AliExpressLinks; // Can be a single string or an object with multiple links
  videoGuide?: string;
  writtenGuide?: string;
  printedParts?: string;
  imageUrl?: string;
  manufacturer?: string;
}

// Sample parts data
export const parts: Part[] = [
  {
    id: 1,
    name: "Unicorn Hotend",
    description: "The Unicorn Hotend is a high-performance direct replacement for the stock Creality K1 hotend. It features improved heat transfer and supports higher temperatures for more exotic filaments.",
    category: "k1_series",
    subcategory: "hotend",
    filters: ["upgrade", "unicorn"],
    rating: 4.5,
    requiresRoot: true,
    requiresWiring: true,
    requiresPrinting: false,
    requiresTools: true,
    requiresPurchase: true,
    amazonLink: "https://www.amazon.com/example",
    aliExpressLink: {
      global: "https://www.aliexpress.com/example",
      us: "https://www.aliexpress.com/us/example"
    },
    videoGuide: "https://youtube.com/example",
    writtenGuide: "https://example.com/guide",
    imageUrl: "https://placehold.co/600x400?text=Unicorn+Hotend",
  },
  {
    id: 2,
    name: "Eddy Current Probe",
    description: "This upgraded eddy current probe provides more accurate bed leveling on the K1 series printers. Compatible with both metal and non-metal print surfaces.",
    category: "k1_series",
    subcategory: "probe",
    filters: ["upgrade", "eddy-current"],
    rating: 5,
    requiresRoot: true,
    amazonLink: "https://www.amazon.com/example2",
    aliExpressLink: {
      global: "https://www.aliexpress.com/example2",
      us: "https://www.aliexpress.com/us/example2"
    },
    writtenGuide: "https://example.com/guide2",
    printedParts: "https://printables.com/model/example",
    imageUrl: "https://placehold.co/600x400?text=Eddy+Current+Probe",
  },
  {
    id: 3,
    name: "Clicky Probe",
    description: "A mechanical 'clicky' probe replacement for the K2 series. Offers consistent Z-offset and improved reliability over the stock probe.",
    category: "k2_series",
    subcategory: "probe",
    filters: ["upgrade", "clicky"],
    rating: 4,
    requiresRoot: false,
    amazonLink: "https://www.amazon.com/example3",
    videoGuide: "https://youtube.com/example3",
    imageUrl: "https://placehold.co/600x400?text=Clicky+Probe",
  },
  {
    id: 4,
    name: "Stock K1 Extruder",
    description: "Original replacement extruder for the Creality K1 printer. Direct replacement with no modifications required.",
    category: "k1_series",
    subcategory: "extruder",
    filters: ["stock"],
    rating: 3.5,
    requiresRoot: false,
    amazonLink: "https://www.amazon.com/example4",
    aliExpressLink: "https://www.aliexpress.com/example4",
    videoGuide: "https://youtube.com/example4",
    imageUrl: "https://placehold.co/600x400?text=Stock+K1+Extruder",
  },
  {
    id: 5,
    name: "Upgrade K2 Extruder",
    description: "Enhanced extruder for K2 series with improved filament grip and tension control. Reduces stringing and improves print quality.",
    category: "k2_series",
    subcategory: "extruder",
    filters: ["upgrade"],
    rating: 4.5,
    requiresRoot: true,
    aliExpressLink: "https://www.aliexpress.com/example5",
    videoGuide: "https://youtube.com/example5",
    printedParts: "https://printables.com/model/example5",
    imageUrl: "https://placehold.co/600x400?text=Upgrade+K2+Extruder",
  },
  {
    id: 6,
    name: "Nozzle Cleaning Kit",
    description: "Professional nozzle cleaning kit with various tools for maintaining your printer's nozzle. Includes cleaning needles, brushes, and tweezers.",
    category: "tools",
    subcategory: "tools",
    filters: ["stock"],
    rating: 0,
    requiresRoot: false,
    amazonLink: "https://www.amazon.com/example6",
    aliExpressLink: "https://www.aliexpress.com/example6",
    videoGuide: "https://youtube.com/example6",
    imageUrl: "https://placehold.co/600x400?text=Nozzle+Cleaning+Kit",
  },
  {
    id: 7,
    name: "Filament Drying Box",
    description: "Keep your filament dry and print-ready with this specialized drying box. Features adjustable temperature control and fits most standard filament spools.",
    category: "tools",
    subcategory: "tools",
    filters: ["upgrade"],
    rating: 0,
    requiresRoot: false,
    amazonLink: "https://www.amazon.com/example7",
    aliExpressLink: "https://www.aliexpress.com/example7",
    imageUrl: "https://placehold.co/600x400?text=Filament+Drying+Box",
  },
];
